import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { EmailRowData, getStatusBadge, getEventColors, formatYesNoUnknown, formatYesNoUnknownInverse, SourceIcons } from '@/lib/email-formatters';
import { formatRelativeDate } from '@/lib/format-relative-date';
import EmailTable from '@/components/EmailTable';
import EmailFilters, { FilterValues, defaultFilterValues } from '@/components/EmailFilters';
import {
  Loader2,
  Download,
  Mail,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Send,
  MailX,
  Globe,
  Clock,
  History,
  Eye,
  MousePointerClick,
  Activity,
  UserMinus,
  Flag,
  Ban,
  AlertOctagon,
} from 'lucide-react';

interface Stats {
  totalEmails: number;
  totalRequests: number;
  cacheHitRate: number;
  statusBreakdown: {
    deliverable: number;
    risky: number;
    undeliverable: number;
    unknown: number;
  };
  delivered: number;
  bounced: number;
  sent: number;
  opened: number;
  hard_bounced: number;
  soft_bounced: number;
  unsubscribed: number;
  complained: number;
  blocked: number;
}

interface ExplorerFilters {
  search: string;
  status: string;
  reason: string;
  scoreMin: string;
  scoreMax: string;
  domain: string;
  domainFree: string;
  domainDisposable: string;
  domainAcceptAll: string;
  accountRole: string;
  provider: string;
  verificationSource: string;
  toxic: string;
  verifiedFrom: string;
  verifiedTo: string;
  page: number;
  sort: string;
  order: string;
}

const defaultExplorerFilters: ExplorerFilters = {
  search: '',
  status: 'all',
  reason: 'all',
  scoreMin: '',
  scoreMax: '',
  domain: '',
  domainFree: 'all',
  domainDisposable: 'all',
  domainAcceptAll: 'all',
  accountRole: 'all',
  provider: 'all',
  verificationSource: 'all',
  toxic: 'all',
  verifiedFrom: '',
  verifiedTo: '',
  page: 1,
  sort: 'verified_at',
  order: 'desc',
};

interface FilterMeta {
  reasons: string[];
  providers: string[];
  sources: string[];
  domains_top: Array<{ domain: string; count: number }>;
  projects: Array<{ id: string; name: string }>;
}

export default function EmailExplorer() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const timezone = user?.timezone || 'UTC';
  const [explorerFilters, setExplorerFilters] = useState<ExplorerFilters>({ ...defaultExplorerFilters });
  const [pillFilters, setPillFilters] = useState<FilterValues>({ ...defaultFilterValues });
  const [emails, setEmails] = useState<EmailRowData[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<FilterMeta | null>(null);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState<any>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  useEffect(() => {
    api.getEmailsExplorerMeta().then(setMeta).catch(() => {});
  }, []);

  useEffect(() => {
    const project = pillFilters.project !== 'all' ? pillFilters.project : undefined;
    setStatsLoading(true);
    api.getOverviewStats(project)
      .then(setStats)
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, [pillFilters.project]);

  // Map pill filters to explorer filters
  const buildApiFilters = useCallback(() => {
    const f = { ...explorerFilters };
    if (pillFilters.status !== 'all') f.status = pillFilters.status;
    if (pillFilters.provider !== 'all') f.provider = pillFilters.provider;
    if (pillFilters.domainDisposable) f.domainDisposable = 'yes';
    if (pillFilters.domainFree) f.domainFree = 'yes';
    if (pillFilters.accountFullMailbox) (f as any).accountFullMailbox = 'yes';
    if (pillFilters.project !== 'all') (f as any).project = pillFilters.project;
    if (pillFilters.brevoSent) (f as any).brevoSent = 'yes';
    if (pillFilters.brevoDelivered) (f as any).brevoDelivered = 'yes';
    if (pillFilters.brevoOpened) (f as any).brevoOpened = 'yes';
    if (pillFilters.brevoHardBounced) (f as any).brevoHardBounced = 'yes';
    if (pillFilters.brevoSoftBounced) (f as any).brevoSoftBounced = 'yes';
    if (pillFilters.brevoUnsubscribed) (f as any).brevoUnsubscribed = 'yes';
    if (pillFilters.brevoComplained) (f as any).brevoComplained = 'yes';
    if (pillFilters.brevoBlocked) (f as any).brevoBlocked = 'yes';
    if (pillFilters.brevoDeferred) (f as any).brevoDeferred = 'yes';
    if (pillFilters.blocklist !== 'all') (f as any).blocklist = pillFilters.blocklist;
    return f;
  }, [explorerFilters, pillFilters]);

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const f = buildApiFilters();
      const apiFilters: Record<string, any> = {};
      if (f.search) apiFilters.search = f.search;
      if (f.status !== 'all') apiFilters.status = f.status;
      if (f.reason !== 'all') apiFilters.reason = f.reason;
      if (f.scoreMin) apiFilters.scoreMin = f.scoreMin;
      if (f.scoreMax) apiFilters.scoreMax = f.scoreMax;
      if (f.domain) apiFilters.domain = f.domain;
      if (f.domainFree !== 'all') apiFilters.domainFree = f.domainFree;
      if (f.domainDisposable !== 'all') apiFilters.domainDisposable = f.domainDisposable;
      if (f.domainAcceptAll !== 'all') apiFilters.domainAcceptAll = f.domainAcceptAll;
      if (f.accountRole !== 'all') apiFilters.accountRole = f.accountRole;
      if (f.provider !== 'all') apiFilters.provider = f.provider;
      if (f.verificationSource !== 'all') apiFilters.verificationSource = f.verificationSource;
      if (f.toxic !== 'all') apiFilters.toxic = f.toxic;
      if (f.verifiedFrom) apiFilters.verifiedFrom = f.verifiedFrom;
      if (f.verifiedTo) apiFilters.verifiedTo = f.verifiedTo;
      if ((f as any).project) apiFilters.project = (f as any).project;
      if ((f as any).brevoSent) apiFilters.brevoSent = 'yes';
      if ((f as any).brevoDelivered) apiFilters.brevoDelivered = 'yes';
      if ((f as any).brevoOpened) apiFilters.brevoOpened = 'yes';
      if ((f as any).brevoHardBounced) apiFilters.brevoHardBounced = 'yes';
      if ((f as any).brevoSoftBounced) apiFilters.brevoSoftBounced = 'yes';
      if ((f as any).brevoUnsubscribed) apiFilters.brevoUnsubscribed = 'yes';
      if ((f as any).brevoComplained) apiFilters.brevoComplained = 'yes';
      if ((f as any).brevoBlocked) apiFilters.brevoBlocked = 'yes';
      if ((f as any).brevoDeferred) apiFilters.brevoDeferred = 'yes';
      if ((f as any).accountFullMailbox) apiFilters.accountFullMailbox = 'yes';
      if ((f as any).blocklist) apiFilters.blocklist = (f as any).blocklist;
      apiFilters.page = f.page;
      apiFilters.sort = f.sort;
      apiFilters.order = f.order;

      const data = await api.getEmailsExplorer(apiFilters);
      setEmails(data.emails);
      setTotal(data.total);
      setPages(data.pages);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [buildApiFilters]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  const handleSort = (field: string) => {
    setExplorerFilters((prev) => ({
      ...prev,
      sort: field,
      order: prev.sort === field && prev.order === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setExplorerFilters((prev) => ({ ...prev, page }));
  };

  const handlePillApply = (filters: FilterValues) => {
    setPillFilters(filters);
    setExplorerFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handlePillReset = () => {
    setPillFilters({ ...defaultFilterValues });
    setExplorerFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleRowClick = (row: EmailRowData) => {
    setDrawerOpen(true);
    setDrawerData(null);
    setDrawerLoading(true);
    api.getEmailByAddress(row.email)
      .then(setDrawerData)
      .catch(() => {})
      .finally(() => setDrawerLoading(false));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const f = buildApiFilters();
      const apiFilters: Record<string, any> = { ...f };
      delete apiFilters.page;
      await api.startEmailsExport(apiFilters);
      navigate('/exports');
    } catch (err) {
      alert('Failed to create export: ' + (err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Email Explorer</h2>
        <p className="text-muted-foreground">
          {total.toLocaleString()} emails found
        </p>
      </div>

      {meta && meta.projects.length > 0 && (
        <div className="flex items-center gap-1 border-b">
          <button
            onClick={() => handlePillApply({ ...pillFilters, project: 'all' })}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              pillFilters.project === 'all'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            All
          </button>
          {meta.projects.map((p) => (
            <button
              key={p.id}
              onClick={() => handlePillApply({ ...pillFilters, project: p.id })}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                pillFilters.project === p.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      {stats && (() => {
        const total = stats.totalEmails;
        const deliverable = Math.min(stats.statusBreakdown?.deliverable || 0, total);
        const undeliverable = Math.min(stats.statusBreakdown?.undeliverable || 0, total);
        const pct = (v: number) => total > 0 ? ((v / total) * 100).toFixed(1) + '%' : '0%';
        return (
          <div className="space-y-3">
            {/* Verification statuses */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[
                { title: 'Total Emails', value: total, icon: Mail, color: 'text-blue-600' },
                { title: 'Deliverable', value: deliverable, icon: CheckCircle, color: 'text-green-600', percent: pct(deliverable) },
                { title: 'Risky', value: stats.statusBreakdown?.risky || 0, icon: AlertTriangle, color: 'text-amber-600' },
                { title: 'Undeliverable', value: undeliverable, icon: XCircle, color: 'text-red-600', percent: pct(undeliverable) },
              ].map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <Card key={i} className={statsLoading ? 'opacity-50' : ''}>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        {stat.title}
                      </CardTitle>
                      {statsLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : (
                        <Icon className={`h-4 w-4 ${stat.color}`} />
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                      </div>
                      {'percent' in stat && stat.percent && (
                        <p className="text-xs text-muted-foreground mt-1">{stat.percent} of total</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            {/* Delivery statistics */}
            <div className="grid gap-2 grid-cols-4 lg:grid-cols-8">
              {[
                { title: 'Sent', value: stats.sent, icon: Send, color: 'text-gray-600' },
                { title: 'Delivered', value: stats.delivered, icon: CheckCircle, color: 'text-emerald-600' },
                { title: 'Opened', value: stats.opened, icon: Eye, color: 'text-blue-600' },
                { title: 'Hard Bounce', value: stats.hard_bounced, icon: MailX, color: 'text-red-600' },
                { title: 'Soft Bounce', value: stats.soft_bounced, icon: AlertTriangle, color: 'text-orange-500' },
                { title: 'Unsubscribed', value: stats.unsubscribed, icon: UserMinus, color: 'text-orange-600' },
                { title: 'Complained', value: stats.complained, icon: Flag, color: 'text-rose-600' },
                { title: 'Blocked', value: stats.blocked, icon: Ban, color: 'text-slate-600' },
              ].map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <div key={i} className={`rounded-lg border bg-card p-3 ${statsLoading ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon className={`h-3.5 w-3.5 ${stat.color}`} />
                      <span className="text-xs text-muted-foreground truncate">{stat.title}</span>
                    </div>
                    <div className="text-lg font-bold">{stat.value.toLocaleString()}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div className="flex items-center justify-between">
        <EmailFilters
          filters={pillFilters}
          onApply={handlePillApply}
          onReset={handlePillReset}
          providers={meta?.providers || []}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={exporting || total === 0}
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export CSV
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <EmailTable
            emails={emails}
            loading={loading}
            page={explorerFilters.page}
            pages={pages}
            total={total}
            onPageChange={handlePageChange}
            sort={explorerFilters.sort}
            order={explorerFilters.order}
            onSort={handleSort}
            onRowClick={handleRowClick}
            timezone={timezone}
          />
        </CardContent>
      </Card>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
          {drawerLoading && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {!drawerLoading && drawerData && (() => {
            const details = drawerData.details;
            const brevoSummary = drawerData.brevoSummary;
            const brevoEvents = drawerData.brevoEvents || [];
            const history = drawerData.history || [];
            const projects = drawerData.projects || [];

            // Группировка событий по проектам
            const projectsMap: Record<string, string> = {};
            projects.forEach((p: { id: string; name: string }) => { projectsMap[p.id] = p.name; });
            const eventsByProject = brevoEvents.reduce((acc: Record<string, any[]>, ev: any) => {
              const projectId = ev.project_id || 'unknown';
              if (!acc[projectId]) acc[projectId] = [];
              acc[projectId].push(ev);
              return acc;
            }, {} as Record<string, any[]>);
            const projectIds = Object.keys(eventsByProject);
            const hasMultipleProjects = projectIds.length > 1;

            return (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-3 font-mono text-base">
                    {drawerData.email}
                    {getStatusBadge(drawerData.status || 'unknown')}
                  </SheetTitle>
                  <SheetDescription className="flex items-center gap-2">
                    <SourceIcons row={{ has_usebouncer: drawerData.has_usebouncer, has_brevo: drawerData.has_brevo }} />
                  </SheetDescription>
                </SheetHeader>

                <div className="space-y-6 mt-6">
                  {/* Top Cards */}
                  {details && (
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Score', value: details.score !== null ? `${details.score}%` : '-' },
                        { label: 'Reason', value: details.reason || '-' },
                        { label: 'Provider', value: details.provider || '-' },
                        { label: 'Domain', value: details.domain_name || '-' },
                      ].map((item) => (
                        <div key={item.label} className="rounded-lg border p-3">
                          <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                          <p className="text-sm font-mono">{item.value}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Domain & Account Info */}
                  {details && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          Domain Info
                        </h4>
                        <div className="rounded-lg border overflow-hidden">
                          <div className="grid grid-cols-2 gap-px bg-muted">
                            <div className="bg-background p-3">
                              <p className="text-xs text-muted-foreground mb-1">Accept All</p>
                              {formatYesNoUnknown(details.domain_accept_all)}
                            </div>
                            <div className="bg-background p-3">
                              <p className="text-xs text-muted-foreground mb-1">Disposable</p>
                              {formatYesNoUnknown(details.domain_disposable)}
                            </div>
                            <div className="bg-background p-3">
                              <p className="text-xs text-muted-foreground mb-1">Free</p>
                              {formatYesNoUnknownInverse(details.domain_free)}
                            </div>
                            <div className="bg-background p-3">
                              <p className="text-xs text-muted-foreground mb-1">DNS</p>
                              <p className="font-mono text-xs">{details.dns_type || '-'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Account Info
                        </h4>
                        <div className="rounded-lg border overflow-hidden">
                          <div className="grid grid-cols-2 gap-px bg-muted">
                            <div className="bg-background p-3">
                              <p className="text-xs text-muted-foreground mb-1">Role</p>
                              {formatYesNoUnknown(details.account_role)}
                            </div>
                            <div className="bg-background p-3">
                              <p className="text-xs text-muted-foreground mb-1">Disabled</p>
                              {formatYesNoUnknown(details.account_disabled)}
                            </div>
                            <div className="bg-background p-3">
                              <p className="text-xs text-muted-foreground mb-1">Full Mailbox</p>
                              {formatYesNoUnknown(details.account_full_mailbox)}
                            </div>
                            <div className="bg-background p-3">
                              <p className="text-xs text-muted-foreground mb-1">Toxic</p>
                              <p className="font-mono text-xs">{details.toxic || '-'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Delivery Events */}
                  {drawerData.has_brevo && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <Send className="h-4 w-4" />
                        Delivery Events
                      </h4>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: 'Sent', value: brevoSummary.sent, icon: Send, color: 'text-gray-600' },
                          { label: 'Delivered', value: brevoSummary.delivered, icon: CheckCircle, color: 'text-green-600' },
                          { label: 'Opened', value: brevoSummary.opened, icon: Eye, color: 'text-blue-600' },
                          { label: 'Clicked', value: brevoSummary.clicked, icon: MousePointerClick, color: 'text-purple-600' },
                          { label: 'Hard Bounce', value: brevoSummary.hard_bounced, icon: AlertOctagon, color: 'text-red-600' },
                          { label: 'Soft Bounce', value: brevoSummary.soft_bounced, icon: AlertTriangle, color: 'text-orange-500' },
                          { label: 'Unsub', value: brevoSummary.unsubscribed, icon: UserMinus, color: 'text-orange-600' },
                          { label: 'Complained', value: brevoSummary.complained, icon: Flag, color: 'text-rose-600' },
                          { label: 'Blocked', value: brevoSummary.blocked, icon: Ban, color: 'text-slate-600' },
                          { label: 'Deferred', value: brevoSummary.deferred, icon: Clock, color: 'text-amber-600' },
                        ].map((s) => (
                          <div key={s.label} className="rounded-lg border p-2">
                            <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
                            <div className="flex items-center gap-1.5">
                              <s.icon className={`h-3.5 w-3.5 ${s.color}`} />
                              <span className="text-sm font-bold">{s.value}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      {brevoEvents.length > 0 && (
                        <>
                          <Separator />
                          <div className="space-y-2">
                            <h4 className="text-sm font-semibold flex items-center gap-2">
                              <Activity className="h-4 w-4" />
                              Timeline
                            </h4>

                            {hasMultipleProjects ? (
                              <div className="space-y-3">
                                {projectIds.map((projectId) => (
                                  <div key={projectId}>
                                    <div className="text-xs font-medium text-muted-foreground mb-1">
                                      {projectsMap[projectId] || projectId}
                                    </div>
                                    <div className="rounded-lg border overflow-hidden">
                                      {eventsByProject[projectId].map((ev: any, i: number) => {
                                        const colors = getEventColors(ev.event);
                                        const Icon = colors.icon;
                                        return (
                                          <div key={`${ev.message_id}-${ev.event}-${i}`} className={`flex items-center gap-2 px-2.5 py-1.5 text-xs ${i % 2 === 0 ? 'bg-muted/30' : ''}`}>
                                            <Icon className={`h-3 w-3 flex-shrink-0 ${colors.text}`} />
                                            <span className={`font-medium ${colors.text} w-20 flex-shrink-0`}>{colors.label}</span>
                                            <span className="text-muted-foreground truncate flex-1">{ev.reason || ev.subject || ''}</span>
                                            <span className="text-muted-foreground whitespace-nowrap text-[11px]">{formatRelativeDate(ev.date, timezone)}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="rounded-lg border overflow-hidden">
                                {brevoEvents.map((ev: any, i: number) => {
                                  const colors = getEventColors(ev.event);
                                  const Icon = colors.icon;
                                  return (
                                    <div key={`${ev.message_id}-${ev.event}-${i}`} className={`flex items-center gap-2 px-2.5 py-1.5 text-xs ${i % 2 === 0 ? 'bg-muted/30' : ''}`}>
                                      <Icon className={`h-3 w-3 flex-shrink-0 ${colors.text}`} />
                                      <span className={`font-medium ${colors.text} w-20 flex-shrink-0`}>{colors.label}</span>
                                      <span className="text-muted-foreground truncate flex-1">{ev.reason || ev.subject || ''}</span>
                                      <span className="text-muted-foreground whitespace-nowrap text-[11px]">{formatRelativeDate(ev.date, timezone)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Verification History */}
                  {history.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <History className="h-4 w-4" />
                        Verification History
                      </h4>
                      <div className="space-y-2">
                        {history.map((h: any) => (
                          <div key={h.id} className="rounded-lg border p-2.5 space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">{h.project_name || 'Unknown'}</span>
                              <Badge variant={h.cached ? 'secondary' : 'outline'} className="text-xs">
                                {h.cached ? 'Cached' : 'Fresh'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>{formatRelativeDate(h.created_at, timezone)}</span>
                              {h.response_time_ms !== null && <span>{h.response_time_ms}ms</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Metadata */}
                  {details && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Metadata
                      </h4>
                      <div className="rounded-lg border p-3 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Verified At</span>
                          <span className="font-mono text-xs">{formatRelativeDate(details.verified_at, timezone)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Source</span>
                          <span className="font-mono text-xs">{details.verification_source}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Verifications</span>
                          <span className="font-mono text-xs">{details.verification_count}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

    </div>
  );
}
